import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { bodyOf, kindOf, looksLikeHtml, stripMarkdown, type StudioItem } from "./helpers";
import { buildSrcDoc, countSources, splitArticleHtml, type SplitArticle } from "./html";
import { LedgerBody, ledgerStageSummary } from "./ledger-view";
import { Markdown } from "./markdown";
import type { LedgerView } from "./quality";

export type BodyFormat = "html" | "markdown" | "text";

export interface PreparedBody {
  format: BodyFormat;
  text: string;
  article: SplitArticle | null;
}

/** Decide how to render an item's body and, for HTML, split it into article / schema / sources. */
export function prepareBody(item: StudioItem, title: string): PreparedBody {
  const text = bodyOf(item);
  if (looksLikeHtml(text)) {
    return { format: "html", text, article: splitArticleHtml(text, title) };
  }
  const format: BodyFormat = kindOf(item) === "social" ? "text" : "markdown";
  if (format === "markdown") {
    // Older packages keep "# Title" as the first line of the body; the decision header already shows it.
    const m = text.match(/^\s*#\s+(.+?)[ \t]*(?:\r?\n)+/);
    const norm = (v: string) => stripMarkdown(v).replace(/\s+/g, " ").trim().toLowerCase();
    if (m && norm(m[1]) === norm(title)) return { format, text: text.slice(m[0].length), article: null };
  }
  return { format, text, article: null };
}

// ── Collapsed side panels ────────────────────────────────────────────────────

function Panel({ title, hint, testId, children }: { title: string; hint?: string; testId: string; children: React.ReactNode }) {
  return (
    <details className="group rounded-xl border border-border bg-card/60" data-testid={testId}>
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
        <ChevronRight size={16} className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
        <span>{title}</span>
        {hint && <span className="ml-auto min-w-0 text-right text-xs font-normal text-muted-foreground">{hint}</span>}
      </summary>
      <div className="border-t border-border px-4 py-3">{children}</div>
    </details>
  );
}

/** The sources ledger, with its `stage:` lines drawn as badges. The header says how many facts it holds and how many rest on law that is not in force yet. */
function SourcesPanel({ text }: { text: string }) {
  const count = countSources(text);
  const { notInForce } = ledgerStageSummary(text);
  const hint = [count ? `${count} sourced facts` : null, notInForce > 0 ? `${notInForce} not in force yet` : null].filter(Boolean).join(" · ");
  return (
    <Panel title="Sources" hint={hint || undefined} testId="panel-sources">
      <div className="max-h-[60vh] overflow-y-auto pr-1" data-testid="ledger-body">
        <LedgerBody text={text} />
      </div>
    </Panel>
  );
}

// ── Article in a script-less sandboxed iframe ───────────────────────────────

/**
 * The article HTML has already been sanitised. It is rendered in an iframe that cannot run
 * scripts (`sandbox` without `allow-scripts`). `allow-same-origin` is only there so we can
 * measure the document and size the frame to its content; with no scripts allowed it gives the
 * document no way to reach the app.
 */
export function ArticleFrame({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(480);
  const srcDoc = useMemo(() => buildSrcDoc(html), [html]);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    let ro: ResizeObserver | null = null;
    let raf = 0;
    const measure = () => {
      const body = frame.contentDocument?.body;
      if (!body) return;
      const h = Math.ceil(body.getBoundingClientRect().height);
      if (h > 0) setHeight((prev) => (prev === h + 4 ? prev : h + 4));
    };
    // Resizing the frame inside a ResizeObserver callback makes the browser report
    // "ResizeObserver loop completed with undelivered notifications" (a dev-overlay error on narrow screens),
    // so the height is applied on the next animation frame instead.
    const measureNextFrame = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    const attach = () => {
      measure();
      ro?.disconnect();
      const body = frame.contentDocument?.body;
      if (body && typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(measureNextFrame);
        ro.observe(body);
      }
    };
    frame.addEventListener("load", attach);
    // srcdoc may already have loaded by the time this effect runs.
    if (frame.contentDocument?.readyState === "complete") attach();
    window.addEventListener("resize", measureNextFrame);
    return () => {
      frame.removeEventListener("load", attach);
      window.removeEventListener("resize", measureNextFrame);
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [srcDoc]);

  return (
    <iframe
      ref={ref}
      title={`Preview: ${title}`}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      scrolling="no"
      referrerPolicy="no-referrer"
      style={{ height }}
      className="block w-full rounded-xl border border-border bg-white"
      data-testid="article-frame"
    />
  );
}

// ── The reader ──────────────────────────────────────────────────────────────

/**
 * `ledger` is the item's own sources ledger (`sourceContext.ledger`); the ledger comment inside the article body is the fallback,
 * so a piece that predates the field still shows its sources.
 */
export function BodyReader({ prepared, title, ledger = null }: { prepared: PreparedBody; title: string; ledger?: LedgerView | null }) {
  const { format, text, article } = prepared;
  const sourcesText = ledger?.text ?? article?.sources ?? null;
  const sourcesPanel = sourcesText ? <SourcesPanel text={sourcesText} /> : null;

  if (!text.trim()) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="body-empty">
        There is no text on this item yet.
      </div>
    );
  }

  if (format === "html" && article) {
    return (
      <div className="space-y-3" data-testid="body-html">
        <ArticleFrame html={article.html} title={title} />
        {sourcesPanel}
        {article.jsonLd.length > 0 && (
          <Panel
            title="Schema (JSON-LD)"
            hint={`${article.jsonLd.length} block${article.jsonLd.length === 1 ? "" : "s"} for search engines`}
            testId="panel-schema"
          >
            <p className="mb-3 text-xs text-muted-foreground">
              Behind-the-scenes data that helps search engines understand the article. It is not shown to readers.
            </p>
            <div className="space-y-3">
              {article.jsonLd.map((b, i) => (
                <div key={i}>
                  <p className="mb-1 text-xs font-semibold text-foreground">{b.type}</p>
                  <pre className="max-h-72 overflow-auto rounded-lg bg-muted/40 p-3 text-[11px] leading-relaxed text-muted-foreground">
                    {b.text}
                  </pre>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    );
  }

  if (format === "markdown") {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-border bg-card/60 px-4 py-5 sm:px-8" data-testid="body-markdown">
          <Markdown>{text}</Markdown>
        </div>
        {sourcesPanel}
      </div>
    );
  }

  // Plain text (social posts): keep the writer's line breaks, nothing else.
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-3">
      <div className="space-y-4 rounded-xl border border-border bg-card/60 px-4 py-5 sm:px-8" data-testid="body-text">
        {paragraphs.map((p, i) => (
          <p key={i} className="whitespace-pre-wrap break-words text-[15px] leading-7 text-foreground/90">
            {p}
          </p>
        ))}
      </div>
      {sourcesPanel}
    </div>
  );
}
